// cardioFitness.js — longitudinal cardiovascular-fitness read from the HR
// signals the app already collects. Snapshots (today's RHR, this workout's
// HR recovery) say nothing about direction; fitness lives in the TREND:
//   • resting HR falling over weeks      = aerobic base improving
//   • walking HR falling at same effort  = improving
//   • post-workout HR recovery rising    = conditioning improving
//   • VO₂max rising                       = improving
// Pure read over localStorage (calorieLog daily values + sirat-workouts HR
// recovery), so it's safe to call from the Readiness card, Today, the AI
// Coach context, and the workout generator without threading state.

const avg = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

function loadLog() { try { return JSON.parse(localStorage.getItem("calorieLog") || "{}"); } catch { return {}; } }
function loadWorkouts() { try { return JSON.parse(localStorage.getItem("sirat-workouts") || "[]"); } catch { return []; } }

// Split a per-day calorieLog metric into a recent window and the prior window
// behind it. recentDays = trailing window; priorDays = the block before it.
function dayMetricWindows(log, field, recentDays, priorDays) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const recent = [], prior = [];
  for (const [d, entry] of Object.entries(log || {})) {
    const v = entry?.[field];
    if (!(v > 0)) continue;
    const dt = new Date(`${d}T00:00:00`);
    if (isNaN(dt.getTime())) continue;
    const ageDays = Math.round((today - dt) / 86400000);
    if (ageDays < 0) continue;
    if (ageDays < recentDays) recent.push(v);
    else if (ageDays < recentDays + priorDays) prior.push(v);
  }
  return { recent, prior };
}

// Classify a recent-vs-prior delta into a direction. `lowerIsBetter` flips the
// sense (RHR/walking HR down = good; HR recovery/VO₂max up = good). `dead` is
// the noise band that counts as "stable".
function classify(recentAvg, priorAvg, { lowerIsBetter, dead }) {
  if (recentAvg == null || priorAvg == null) return { dir: "unknown", deltaBpm: null };
  const delta = +(recentAvg - priorAvg).toFixed(1);
  let dir = "stable";
  if (Math.abs(delta) >= dead) {
    const better = lowerIsBetter ? delta < 0 : delta > 0;
    dir = better ? "improving" : "declining";
  }
  return { dir, deltaBpm: delta, recent: +recentAvg.toFixed(1), prior: +priorAvg.toFixed(1) };
}

// recentDays / priorDays default to a 7-day trailing window vs the 3 weeks
// behind it — enough to denoise day-to-day variation in RHR / walking HR.
export function computeCardioFitness({ recentDays = 7, priorDays = 21, minReadings = 3 } = {}) {
  const log = loadLog();

  const rhrW  = dayMetricWindows(log, "restingHeartRate", recentDays, priorDays);
  const walkW = dayMetricWindows(log, "walkingHrAvg",     recentDays, priorDays);

  const enough = (w) => w.recent.length >= minReadings && w.prior.length >= minReadings;
  const rhr  = enough(rhrW)  ? classify(avg(rhrW.recent),  avg(rhrW.prior),  { lowerIsBetter: true,  dead: 1.5 }) : { dir: "unknown", deltaBpm: null };
  const walk = enough(walkW) ? classify(avg(walkW.recent), avg(walkW.prior), { lowerIsBetter: true,  dead: 1.5 }) : { dir: "unknown", deltaBpm: null };

  // HR recovery (1-min) lives on completed workouts, not the daily log.
  const recoveries = loadWorkouts()
    .filter(w => w?.watch && (w.watch.hrRecoveryBpm > 0))
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
    .map(w => w.watch.hrRecoveryBpm);
  let hrRecovery = { dir: "unknown", deltaBpm: null, n: recoveries.length };
  if (recoveries.length >= 4) {
    const recent = recoveries.slice(0, 3);
    const prior  = recoveries.slice(3, 8);
    hrRecovery = { ...classify(avg(recent), avg(prior), { lowerIsBetter: false, dead: 3 }), n: recoveries.length };
  }

  // VO₂max direction from the most recent 30-day delta we have stored.
  const latestVo2 = (() => {
    const days = Object.keys(log).sort().reverse();
    for (const d of days) { const v = log[d]?.vo2Max; if (v > 0) return { value: v, delta30d: log[d]?.vo2MaxDelta30d || 0 }; }
    return null;
  })();
  const vo2Max = latestVo2
    ? { value: latestVo2.value, delta30d: latestVo2.delta30d,
        dir: latestVo2.delta30d >= 0.5 ? "improving" : latestVo2.delta30d <= -0.5 ? "declining" : "stable" }
    : { dir: "unknown", value: null, delta30d: null };

  const signals = { rhr, walkingHr: walk, hrRecovery, vo2Max };
  const dirs = Object.values(signals).map(s => s.dir).filter(d => d !== "unknown");
  const improving = dirs.filter(d => d === "improving").length;
  const declining = dirs.filter(d => d === "declining").length;

  let verdict = "building"; // not enough trend data yet
  if (dirs.length >= 2) {
    verdict = improving > declining ? "improving" : declining > improving ? "declining" : "stable";
  }

  // Compact human summary citing whichever signals have a direction.
  const bits = [];
  if (rhr.dir !== "unknown" && rhr.prior != null)  bits.push(`RHR ${rhr.prior}→${rhr.recent}`);
  if (walk.dir !== "unknown" && walk.prior != null) bits.push(`walking HR ${walk.prior}→${walk.recent}`);
  if (hrRecovery.dir !== "unknown" && hrRecovery.prior != null) bits.push(`HR-recovery ${hrRecovery.prior}→${hrRecovery.recent}`);
  if (vo2Max.dir !== "unknown" && vo2Max.delta30d) bits.push(`VO₂max ${vo2Max.delta30d > 0 ? "+" : ""}${vo2Max.delta30d}`);

  return {
    hasData: dirs.length >= 1,
    verdict,                       // improving | declining | stable | building
    summary: bits.join(" · "),
    signals,
    windowLabel: `${recentDays}d vs prior ${priorDays}d`,
  };
}
